import { ChangeEvent } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DCUIContent, DCUIContentGeneral } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { DCUIThemingTextContentValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { InputFormGroup } from "../../../../../common/input-form-group/input-form-group";
import { TextareaFormGroup } from "../../../../../common/textarea-form-group/textarea-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingTextContentGeneralSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    dcuiContent: DCUIContent;
    inheritedDCUIContent: DCUIContent;
    pageLevel: PageLevelTypes;
    validationErrors: DCUIThemingTextContentValidationErrors;
    updateDCUIContentPropertyValue: (dcuiContent: DCUIContent) => void;
};

export const DCUIThemingTextContentGeneralSection = (props: DCUIThemingTextContentGeneralSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        dcuiContent,
        inheritedDCUIContent,
        pageLevel,
        validationErrors,
        updateDCUIContentPropertyValue
    } = props;

    const updateDCUIContentGeneralFieldValue = (
        key: keyof DCUIContentGeneral,
        value: string
    ) => {
        const newPropertyValue: DCUIContent = {
            ...dcuiContent,
            general: {
                ...dcuiContent.general,
                [key]: value,
            },
        };
        updateDCUIContentPropertyValue(newPropertyValue);
    };

    const handleQualityAdviceMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentGeneralFieldValue("qualityAdviceMessage", event.target.value);
    };

    const handleResetQualityAdviceMessage = () => {
        updateDCUIContentGeneralFieldValue("qualityAdviceMessage", inheritedDCUIContent.general.qualityAdviceMessage);
    };

    const handleContinueButtonTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentGeneralFieldValue("continueButtonText", event.target.value);
    };

    const handleResetContinueButtonText = () => {
        updateDCUIContentGeneralFieldValue("continueButtonText", inheritedDCUIContent.general.continueButtonText);
    };

    const handleDiscardButtonTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentGeneralFieldValue("discardButtonText", event.target.value);
    };

    const handleResetDiscardButtonText = () => {
        updateDCUIContentGeneralFieldValue("discardButtonText", inheritedDCUIContent.general.discardButtonText);
    };

    const generalResources = resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.textContent.sections.general;

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.general.qualityAdviceMessage}
                currentThemingPropertyValue={dcuiContent.general.qualityAdviceMessage}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={"document-capture-text-content-quality-advice-message-input"}
                    automationId={"document-capture-text-content-quality-advice-message-input"}
                    label={generalResources.inputs.qualityAdviceMessage.label}
                    ariaLabel={generalResources.inputs.qualityAdviceMessage.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleQualityAdviceMessageChange}
                    value={dcuiContent.general.qualityAdviceMessage || ""}
                    disabled={isDisabled}
                    isRequired={false}
                    afterLabelTooltipText={generalResources.inputs.qualityAdviceMessage.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetQualityAdviceMessage}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.general.continueButtonText}
                currentThemingPropertyValue={dcuiContent.general.continueButtonText}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"document-capture-text-content-continue-button-input"}
                    automationId={"document-capture-text-content-continue-button-input"}
                    label={generalResources.inputs.continueButtonText.label}
                    ariaLabel={generalResources.inputs.continueButtonText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleContinueButtonTextChange}
                    value={dcuiContent.general.continueButtonText || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={generalResources.inputs.continueButtonText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetContinueButtonText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.continueButtonError}

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.general.discardButtonText}
                currentThemingPropertyValue={dcuiContent.general.discardButtonText}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"document-capture-text-content-discard-button-input"}
                    automationId={"document-capture-text-content-discard-button-input"}
                    label={generalResources.inputs.discardButtonText.label}
                    ariaLabel={generalResources.inputs.discardButtonText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleDiscardButtonTextChange}
                    value={dcuiContent.general.discardButtonText || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={generalResources.inputs.discardButtonText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetDiscardButtonText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.discardButtonError}
        </div>
    );
};